import { type JSX, type ReactNode, useEffect } from 'react';
import { useDebouncedState } from '@mantine/hooks';
import clsx from 'clsx';

import { TextWithTooltip } from '../../tooltip/TextWithTooltip';
import {
  HorizontalNavigation,
  type NavigationItemData,
} from '../horizontalnavigation/HorizontalNavigation';

import styles from './topNavigation.module.scss';

export type TopNavigationLinkRenderer = (
  item: NavigationItemData,
  close: () => void
) => JSX.Element;

export type TopNavigationProps = {
  activeColumnIndex?: number;
  activeSlug: string;
  bodyOpenClassName?: string;
  Link?: TopNavigationLinkRenderer;
  navigation: NavigationItemData[];
  trailingActions?: ReactNode;
};

export type TopNavigationLinkContentOptions = {
  activeSlug: string;
  className?: string;
  item: NavigationItemData;
  navigation: NavigationItemData[];
};

export function isTopNavigationMainLink(
  item: NavigationItemData,
  navigation: NavigationItemData[]
) {
  return navigation.some((entry) => entry.completeSlug === item.completeSlug);
}

export function getTopNavigationLinkContent({
  activeSlug,
  className,
  item,
  navigation,
}: TopNavigationLinkContentOptions): {
  className: string;
  children: ReactNode;
} {
  const isMainLink = isTopNavigationMainLink(item, navigation);

  return {
    className: clsx(
      styles['subItem'],
      activeSlug === item.completeSlug && styles['subItem--active'],
      isMainLink && styles['subItem--main'],
      className
    ),
    children: (
      <TextWithTooltip
        textProps={{ lineClamp: isMainLink ? 1 : 2 }}
        style={isMainLink ? { wordBreak: 'break-all' } : undefined}
      >
        {item.label}
      </TextWithTooltip>
    ),
  };
}

function DefaultTopNavigationLink({
  activeSlug,
  item,
  navigation,
  onClick,
}: TopNavigationLinkContentOptions & { onClick: () => void }) {
  const { className, children } = getTopNavigationLinkContent({
    activeSlug,
    item,
    navigation,
  });

  return (
    <a
      href="#"
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      {children}
    </a>
  );
}

export function TopNavigation({
  activeColumnIndex,
  activeSlug,
  bodyOpenClassName = 'with-subnavigation--open',
  Link,
  navigation,
  trailingActions,
}: TopNavigationProps) {
  const [isOpen, setOpen] = useDebouncedState(false, 200);

  useEffect(() => {
    document.body.classList.toggle(bodyOpenClassName, isOpen);

    return () => {
      document.body.classList.remove(bodyOpenClassName);
    };
  }, [bodyOpenClassName, isOpen]);

  const close = () => setOpen(false);
  const hasNestedNavigation = navigation.some(
    (item) => item.items != null && item.items.length > 0
  );

  return (
    <header
      onMouseEnter={() => {
        if (hasNestedNavigation) {
          setOpen(true);
        }
      }}
      onMouseLeave={close}
      className={styles['root']}
      data-testid="top-navigation"
    >
      <HorizontalNavigation
        isOpen={isOpen}
        activeColumnIndex={activeColumnIndex}
        navigation={navigation}
        trailingActions={trailingActions}
        Link={(item) =>
          Link ? (
            Link(item, close)
          ) : (
            <DefaultTopNavigationLink
              activeSlug={activeSlug}
              item={item}
              navigation={navigation}
              onClick={close}
            />
          )
        }
      />
      <div className={styles['backdrop']}></div>
    </header>
  );
}
